import { 
  Car, Home, MapPin, Calendar, Gauge, Fuel, Settings, 
  Palette, DollarSign, Eye, Phone, Mail, Building2,
  Bed, Bath, Ruler, CheckCircle, Share2
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import ShareButtons from './ShareButtons';

// Format price with commas
const formatPrice = (price) => {
  if (!price) return '0';
  return Number(price).toLocaleString('en-US');
};

// Get label for dropdown values
const getLabel = (options, value, language) => {
  const option = options.find(opt => opt.id === value);
  return option ? (language === 'ar' ? option.ar : option.en) : value;
};

// Body types
const bodyTypes = [
  { id: 'sedan', ar: 'سيدان', en: 'Sedan' },
  { id: 'suv', ar: 'دفع رباعي SUV', en: 'SUV' },
  { id: 'hatchback', ar: 'هاتشباك', en: 'Hatchback' },
  { id: 'coupe', ar: 'كوبيه', en: 'Coupe' },
  { id: 'convertible', ar: 'مكشوف', en: 'Convertible' },
  { id: 'pickup', ar: 'بيك أب', en: 'Pick Up' },
  { id: 'van', ar: 'فان', en: 'Van' },
  { id: 'wagon', ar: 'ستيشن واغن', en: 'Wagon' }
];

// Fuel types
const fuelTypes = [
  { id: 'petrol', ar: 'بنزين', en: 'Petrol' },
  { id: 'diesel', ar: 'ديزل', en: 'Diesel' },
  { id: 'electric', ar: 'كهربائي', en: 'Electric' },
  { id: 'hybrid', ar: 'هايبرد', en: 'Hybrid' }
];

// Transmission types
const transmissionTypes = [
  { id: 'automatic', ar: 'أوتوماتيك', en: 'Automatic' },
  { id: 'manual', ar: 'يدوي', en: 'Manual' }
];

// Condition options
const conditionOptions = [
  { id: 'excellent', ar: 'ممتازة', en: 'Excellent' },
  { id: 'good', ar: 'جيدة', en: 'Good' },
  { id: 'fair', ar: 'متوسطة', en: 'Fair' }
];

// Emirates
const emirates = [
  { id: 'dubai', ar: 'دبي', en: 'Dubai' },
  { id: 'abudhabi', ar: 'أبوظبي', en: 'Abu Dhabi' },
  { id: 'sharjah', ar: 'الشارقة', en: 'Sharjah' },
  { id: 'ajman', ar: 'عجمان', en: 'Ajman' },
  { id: 'rak', ar: 'رأس الخيمة', en: 'Ras Al Khaimah' },
  { id: 'fujairah', ar: 'الفجيرة', en: 'Fujairah' },
  { id: 'uaq', ar: 'أم القيوين', en: 'Umm Al Quwain' }
];

// Property types
const propertyTypes = [
  { id: 'apartment', ar: 'شقة', en: 'Apartment' },
  { id: 'villa', ar: 'فيلا', en: 'Villa' },
  { id: 'townhouse', ar: 'تاون هاوس', en: 'Townhouse' },
  { id: 'penthouse', ar: 'بنتهاوس', en: 'Penthouse' },
  { id: 'studio', ar: 'استوديو', en: 'Studio' },
  { id: 'land', ar: 'أرض', en: 'Land' },
  { id: 'building', ar: 'مبنى', en: 'Building' },
  { id: 'warehouse', ar: 'مستودع', en: 'Warehouse' },
  { id: 'office', ar: 'مكتب', en: 'Office' },
  { id: 'shop', ar: 'محل تجاري', en: 'Shop' }
];

// Transaction types
const transactionTypes = [
  { id: 'sale', ar: 'للبيع', en: 'For Sale' },
  { id: 'rent', ar: 'للإيجار', en: 'For Rent' },
  { id: 'daily_rent', ar: 'إيجار يومي', en: 'Daily Rent' }
];

// Amenities
const amenities = [
  { id: 'parking', ar: 'موقف سيارات', en: 'Parking' },
  { id: 'gym', ar: 'صالة رياضية', en: 'Gym' },
  { id: 'pool', ar: 'مسبح', en: 'Swimming Pool' },
  { id: 'security', ar: 'أمن 24/7', en: '24/7 Security' },
  { id: 'garden', ar: 'حديقة', en: 'Garden' },
  { id: 'balcony', ar: 'شرفة', en: 'Balcony' },
  { id: 'maid_room', ar: 'غرفة خادمة', en: 'Maid Room' },
  { id: 'storage', ar: 'غرفة تخزين', en: 'Storage Room' },
  { id: 'central_ac', ar: 'تكييف مركزي', en: 'Central A/C' },
  { id: 'concierge', ar: 'خدمة كونسيرج', en: 'Concierge' },
  { id: 'children_area', ar: 'منطقة ألعاب أطفال', en: 'Children Play Area' },
  { id: 'bbq', ar: 'منطقة شواء', en: 'BBQ Area' },
  { id: 'sea_view', ar: 'إطلالة بحرية', en: 'Sea View' },
  { id: 'city_view', ar: 'إطلالة على المدينة', en: 'City View' },
  { id: 'pets_allowed', ar: 'مسموح بالحيوانات', en: 'Pets Allowed' },
  { id: 'kitchen_appliances', ar: 'أجهزة مطبخ', en: 'Kitchen Appliances' }
];

// Car Ad Preview Component
export function CarAdPreview({ data, images = [], storeName }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  
  const title = `${data.make || ''} ${data.model || ''} ${data.year || ''}`.trim();
  
  return (
    <div className={`rounded-2xl overflow-hidden border shadow-lg ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
      {/* Preview Badge */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-2 text-sm font-medium">
        <Eye className="h-4 w-4 inline me-2" />
        {language === 'ar' ? 'معاينة الإعلان - هكذا سيظهر للعملاء' : 'Ad Preview - This is how customers will see it'}
      </div>
      
      {/* Image Gallery */}
      <div className="relative aspect-video bg-gray-100">
        {images.length > 0 ? (
          <img 
            src={images[0].preview} 
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-100'}`}>
            <Car className="h-20 w-20 text-gray-300" />
          </div>
        )}
        
        {/* Image count badge */}
        {images.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
            {images.length} {language === 'ar' ? 'صور' : 'photos'}
          </div>
        )}
        
        {/* Condition badge */}
        {data.condition && (
          <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-sm font-medium ${
            data.condition === 'excellent' ? 'bg-green-500 text-white' :
            data.condition === 'good' ? 'bg-blue-500 text-white' :
            'bg-amber-500 text-white'
          }`}>
            {getLabel(conditionOptions, data.condition, language)}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5">
        {/* Title & Price */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {title || (language === 'ar' ? 'سيارة جديدة' : 'New Car')}
            </h2>
            {storeName && (
              <p className="text-sm text-gray-500 mt-1">
                {language === 'ar' ? 'بواسطة' : 'by'} {storeName}
              </p>
            )}
          </div>
          <div className="text-left">
            <div className="text-2xl font-bold text-green-600">
              {formatPrice(data.price)} <span className="text-sm">{language === 'ar' ? 'درهم' : 'AED'}</span>
            </div>
          </div>
        </div>
        
        {/* Quick Specs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {data.year && (
            <div className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
              <Calendar className="h-4 w-4 text-blue-500" />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{data.year}</span>
            </div>
          )}
          {data.mileage && (
            <div className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
              <Gauge className="h-4 w-4 text-[#1E9BD9]" />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{formatPrice(data.mileage)} km</span>
            </div>
          )}
          {data.fuelType && (
            <div className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
              <Fuel className="h-4 w-4 text-amber-500" />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{getLabel(fuelTypes, data.fuelType, language)}</span>
            </div>
          )}
          {data.transmission && (
            <div className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
              <Settings className="h-4 w-4 text-gray-500" />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{getLabel(transmissionTypes, data.transmission, language)}</span>
            </div>
          )}
        </div>
        
        {/* Details Grid */}
        <div className={`grid grid-cols-2 gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
          {data.bodyType && (
            <div className="flex justify-between">
              <span className="text-gray-500">{language === 'ar' ? 'الهيكل' : 'Body'}</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{getLabel(bodyTypes, data.bodyType, language)}</span>
            </div>
          )}
          {data.color && (
            <div className="flex justify-between">
              <span className="text-gray-500">{language === 'ar' ? 'اللون' : 'Color'}</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{data.color}</span>
            </div>
          )}
          {data.emirate && (
            <div className="flex justify-between">
              <span className="text-gray-500">{language === 'ar' ? 'الإمارة' : 'Emirate'}</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{getLabel(emirates, data.emirate, language)}</span>
            </div>
          )}
          {data.regionalSpecs && (
            <div className="flex justify-between">
              <span className="text-gray-500">{language === 'ar' ? 'المواصفات' : 'Specs'}</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{data.regionalSpecs === 'gcc' ? (language === 'ar' ? 'خليجي' : 'GCC') : data.regionalSpecs}</span>
            </div>
          )}
        </div>
        
        {/* Description */}
        {data.description && (
          <div className="mt-4">
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {language === 'ar' ? 'الوصف' : 'Description'}
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {data.description}
            </p>
          </div>
        )}
        
        {/* Contact Buttons */}
        <div className="flex gap-3 mt-5">
          <button className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
            <Phone className="h-5 w-5" />
            {language === 'ar' ? 'اتصل الآن' : 'Call Now'}
          </button>
          <button className="flex-1 bg-[#1E9BD9] hover:bg-[#0369A1] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
            <Mail className="h-5 w-5" />
            {language === 'ar' ? 'راسل البائع' : 'Message Seller'}
          </button>
        </div>
        
        {/* Share Buttons */}
        <div className="mt-4">
          <ShareButtons 
            title={title || (language === 'ar' ? 'سيارة للبيع' : 'Car for Sale')}
            description={data.description || `${data.make} ${data.model} ${data.year}`}
            price={data.price}
          />
        </div>
      </div>
    </div>
  );
}

// Real Estate Ad Preview Component
export function RealEstateAdPreview({ data, images = [], storeName }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  
  const title = data.title || data.titleEn || (language === 'ar' ? 'عقار جديد' : 'New Property');
  const selectedAmenities = (data.amenities || []).map(id => amenities.find(a => a.id === id)).filter(Boolean);
  
  return (
    <div className={`rounded-2xl overflow-hidden border shadow-lg ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
      {/* Preview Badge */}
      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-center py-2 text-sm font-medium">
        <Eye className="h-4 w-4 inline me-2" />
        {language === 'ar' ? 'معاينة الإعلان - هكذا سيظهر للعملاء' : 'Ad Preview - This is how customers will see it'}
      </div>
      
      {/* Image Gallery */}
      <div className="relative aspect-video bg-gray-100">
        {images.length > 0 ? (
          <img 
            src={images[0].preview} 
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-100'}`}>
            <Building2 className="h-20 w-20 text-gray-300" />
          </div>
        )}
        
        {/* Transaction type badge */}
        {data.transactionType && (
          <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-sm font-medium ${
            data.transactionType === 'sale' ? 'bg-green-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            {getLabel(transactionTypes, data.transactionType, language)}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5">
        {/* Title & Price */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {title}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <MapPin className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-500">
                {data.area_name && `${data.area_name}, `}
                {getLabel(emirates, data.emirate, language)}
              </span>
            </div>
          </div>
          <div className="text-left">
            <div className="text-2xl font-bold text-green-600">
              {formatPrice(data.price)} <span className="text-sm">{language === 'ar' ? 'درهم' : 'AED'}</span>
            </div>
            {data.transactionType === 'rent' && (
              <span className="text-xs text-gray-500">/ {language === 'ar' ? 'سنوياً' : 'yearly'}</span>
            )}
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {data.bedrooms && (
            <div className={`text-center p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-blue-50'}`}>
              <Bed className="h-5 w-5 text-blue-500 mx-auto mb-1" />
              <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{data.bedrooms}</span>
              <p className="text-xs text-gray-500">{language === 'ar' ? 'غرف' : 'Beds'}</p>
            </div>
          )}
          {data.bathrooms && (
            <div className={`text-center p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-cyan-50'}`}>
              <Bath className="h-5 w-5 text-cyan-500 mx-auto mb-1" />
              <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{data.bathrooms}</span>
              <p className="text-xs text-gray-500">{language === 'ar' ? 'حمام' : 'Baths'}</p>
            </div>
          )}
          {data.area && (
            <div className={`text-center p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-[#1E9BD9]/10'}`}>
              <Ruler className="h-5 w-5 text-[#1E9BD9] mx-auto mb-1" />
              <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatPrice(data.area)}</span>
              <p className="text-xs text-gray-500">{language === 'ar' ? 'قدم²' : 'sqft'}</p>
            </div>
          )}
          {data.propertyType && (
            <div className={`text-center p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-amber-50'}`}>
              <Home className="h-5 w-5 text-amber-500 mx-auto mb-1" />
              <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {getLabel(propertyTypes, data.propertyType, language)}
              </span>
            </div>
          )}
        </div>
        
        {/* Amenities */}
        {selectedAmenities.length > 0 && (
          <div className="mb-4">
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {language === 'ar' ? 'المرافق' : 'Amenities'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {selectedAmenities.slice(0, 6).map(amenity => (
                <span 
                  key={amenity.id}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-[#0F172A] text-gray-300' : 'bg-gray-100 text-gray-700'}`}
                >
                  <CheckCircle className="h-3 w-3 inline me-1 text-green-500" />
                  {language === 'ar' ? amenity.ar : amenity.en}
                </span>
              ))}
              {selectedAmenities.length > 6 && (
                <span className={`px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-[#0F172A] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                  +{selectedAmenities.length - 6} {language === 'ar' ? 'المزيد' : 'more'}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Description */}
        {(data.description || data.descriptionEn) && (
          <div className="mb-4">
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {language === 'ar' ? 'الوصف' : 'Description'}
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'ar' ? data.description : data.descriptionEn}
            </p>
          </div>
        )}
        
        {/* Contact Buttons */}
        <div className="flex gap-3 mt-5">
          <button className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
            <Phone className="h-5 w-5" />
            {language === 'ar' ? 'اتصل الآن' : 'Call Now'}
          </button>
          <button className="flex-1 bg-[#1E9BD9] hover:bg-[#0369A1] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
            <Mail className="h-5 w-5" />
            {language === 'ar' ? 'راسل البائع' : 'Message Seller'}
          </button>
        </div>
        
        {/* Share Buttons */}
        <div className="mt-4">
          <ShareButtons 
            title={title}
            description={language === 'ar' ? data.description : data.descriptionEn}
            price={data.price}
          />
        </div>
      </div>
    </div>
  );
}
